import React, { useState } from 'react';
import { Card } from '../../../../../../components/ui/card';
import {
  Calendar,
  MapPin,
  User,
  Building2,
  Activity,
  FileText,
  HardHat,
} from 'lucide-react';
import type { Project } from '../../../../types/project';
import UpdateStatusDialog from '../../../../../../components/ui/UpdateStatusDialog';
import { UpdateProjectStatus } from '../../../../../../redux/Project/saga';
import { fetchProjectList } from '../../../../../../redux/Project/action';
import { useDispatch } from 'react-redux';
import { STATUS_COLORS } from '../../../../../../features/home/constants/filterConstants';
import { InfoField, StatusBadge } from './common';

interface BasicInfoProps {
  project: Project;
  onStatusUpdate?: (projectId: string, newStatus: string) => Promise<void>;
}

export const BasicInfo: React.FC<BasicInfoProps> = ({ project, onStatusUpdate }) => {
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const dispatch = useDispatch();

  const handleStatusUpdate = async (newStatus: string) => {
    if (project.progress !== newStatus) {
      await UpdateProjectStatus(project.projectId, newStatus);
      project.progress = newStatus;
      dispatch(fetchProjectList());
    }
  };
  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric'
  //   });
  // };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="grid grid-cols-1 gap-2" style={{ margin: '20px' }}>
      <Card className="p-6 overflow-hidden">
        <h3 className="text-[14px] font-semibold mb-6">Project Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-5 gap-y-6 lg:gap-x-6 lg:gap-y-8 overflow-hidden min-w-0">
          <div className="min-w-0">
            <InfoField
              icon={Building2}
              label="Project Name"
              value={project.projectName}
            />
          </div>
          <div className="min-w-0">
            <InfoField
              icon={FileText}
              label="Project Number"
              value={project.projectNumber}
            />
          </div>
          <div className="min-w-0">
            <InfoField
              icon={User}
              label="Customer"
              value={project.customer}
            />
          </div>
          <div className="min-w-0">
            <InfoField
              icon={HardHat}
              label="Contractor Name"
              value={project.contractorName || project.contractor_name || ''}
            />
          </div>
          <div className="min-w-0">
            <InfoField
              icon={MapPin}
              label="Location"
              value={project.location}
            />
          </div>

          <div className="min-w-0 flex items-center">
            <Activity className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
            <div className="min-w-0 ml-4 flex-1 overflow-hidden">
              <p className="text-sm font-medium text-gray-500 mb-1">Progress</p>
              <div className="flex items-center gap-2">
                <StatusBadge
                  status={project.progress}
                  className={STATUS_COLORS[project.progress as keyof typeof STATUS_COLORS] || STATUS_COLORS.default}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <InfoField
              icon={Calendar}
              label="Start Date"
              value={formatDate(project.startDate)}
            />
          </div>
          <div className="min-w-0">
            <InfoField
              icon={Calendar}
              label="End Date"
              value={formatDate(project.endDate)}
            />
          </div>
          <div className="min-w-0">
            <InfoField
              icon={User}
              label="Created By"
              value={project.createdBy}
            />
          </div>
          <div className="min-w-0 hidden lg:block" aria-hidden />
        </div>
      </Card>
      
      <UpdateStatusDialog
        open={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        currentStatus={project.progress}
        onUpdate={handleStatusUpdate}
        projectName={project.projectName}
      />
    </div>
  );
}; 